import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../platform';
import { X } from 'lucide-react';
import { Button } from './ui';
import { Spinner } from './ui/spinner';

/**
 * Modal overlay for Telegram Login Widget
 * Used for guest users to log in without leaving the current page
 */
export function LoginModal({ isOpen, onClose }) {
  const { loginWithTelegram, isAuthenticated } = useAuth();
  const [loginError, setLoginError] = useState(null);
  const [botUsername, setBotUsername] = useState(null);

  // Stable callback for Telegram auth
  const handleTelegramAuth = useCallback(async (user) => {
    setLoginError(null);
    const success = await loginWithTelegram(user);
    if (success) {
      onClose();
    } else {
      setLoginError('Login failed. Please try again.');
    }
  }, [loginWithTelegram, onClose]);

  // Fetch bot config on mount
  useEffect(() => {
    if (!isOpen) return;

    fetch('/api/config')
      .then(res => res.json())
      .then(data => {
        if (data.bot_username) {
          setBotUsername(data.bot_username);
        }
      })
      .catch(() => {
        // Fallback to production bot name
        setBotUsername('BuyTelegramAdsBot');
      });
  }, [isOpen]);

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      onClose();
    }
  }, [isAuthenticated, isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-card rounded-xl shadow-xl border p-6 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-3 right-3"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={20} />
        </Button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <img src={process.env.PUBLIC_URL + '/logo192.png'} alt="BuyTelegramAds" className="w-10 h-10" />
          </div>
          <h2 className="text-xl font-semibold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your Telegram account to continue
          </p>
        </div>

        <div className="flex justify-center min-h-[48px]">
          {botUsername ? (
            <TelegramLoginButton
              botName={botUsername}
              onAuth={handleTelegramAuth}
              onError={() => setLoginError('Failed to load login widget')}
            />
          ) : (
            <Spinner className="h-8 w-8 text-primary" />
          )}
        </div>

        {loginError && (
          <div className="mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {loginError}
          </div>
        )}

        <div className="mt-6 pt-4 border-t text-center text-xs text-muted-foreground">
          <p>
            By signing in, you agree to receive notifications.
            <br />
            <a
              href="https://t.me/BuyTelegramAdsBot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Open in Telegram
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Separate component for Telegram Login Button to isolate DOM manipulation
 */
function TelegramLoginButton({ botName, onAuth, onError }) {
  const containerRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    // Prevent double-mounting in strict mode
    if (mountedRef.current) return;
    mountedRef.current = true;

    const container = containerRef.current;
    if (!container) return;

    // Set up the global callback
    window.TelegramLoginWidget = {
      dataOnauth: (user) => onAuth(user)
    };

    // Create script element
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'TelegramLoginWidget.dataOnauth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    script.onerror = onError;

    container.appendChild(script);

    // No cleanup - let the widget remain since removing it causes issues
  }, [botName, onAuth, onError]);

  return <div ref={containerRef} className="min-h-[48px] flex justify-center" />;
}
